import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import API from '../../../../services/API'

// Per-notification-type delivery channels (email / app push). Types and
// saved values come from /account/notification-preferences; app push only
// reaches devices once the user has signed into the mobile app.
const NotificationPreferences = () => {
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [types, setTypes] = useState([])
  const [preferences, setPreferences] = useState({})
  const [hasPushToken, setHasPushToken] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    API.get('/account/notification-preferences')
      .then((res) => {
        if (res.status === 200 && res.data) {
          setTypes(res.data.types ?? [])
          setPreferences(res.data.preferences ?? {})
          setHasPushToken(!!res.data.hasPushToken)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  const groups = useMemo(() => {
    const map = new Map()
    types.forEach((type) => {
      const group = type.group || 'Other'
      if (!map.has(group)) map.set(group, [])
      map.get(group).push(type)
    })
    return [...map.entries()]
  }, [types])

  const toggle = (key, channel) => {
    setSaved(false)
    setDirty(true)
    setPreferences((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [channel]: !prev[key]?.[channel],
      },
    }))
  }

  const handleSave = () => {
    setSaving(true)
    setSaved(false)
    API.put('/account/notification-preferences', { preferences })
      .then((res) => {
        if (res.status === 200) {
          setPreferences(res.data.preferences ?? preferences)
          setSaved(true)
          setDirty(false)
        }
      })
      .finally(() => setSaving(false))
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size="1.8rem" />
      </Box>
    )
  }

  if (fetchError) {
    return (
      <Alert severity="error">
        Unable to load your notification preferences. Please try again later.
      </Alert>
    )
  }

  // The server only returns the notifications this account can be sent
  // (see server helpers/notifications/audience.js). Accounts with no active
  // employee record - booking-portal contacts, subcontractor logins - get
  // nothing back.
  if (!types.length) {
    return (
      <Alert severity="info">
        There are no notifications that apply to your account yet. Notifications
        are added here as your role in AmbuNet changes.
      </Alert>
    )
  }

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Choose how each notification reaches you. Only notifications relevant
        to your role are listed. App notifications are delivered to the AmbuNet
        mobile app
        {hasPushToken
          ? '.'
          : ' — sign in to the app on your phone to enable them.'}
      </Alert>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          alignItems: 'center',
          columnGap: 1,
        }}
      >
        <Box />
        <Tooltip title="Email">
          <Stack alignItems="center" sx={{ width: 64 }}>
            <EmailIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              Email
            </Typography>
          </Stack>
        </Tooltip>
        <Tooltip title="AmbuNet mobile app">
          <Stack alignItems="center" sx={{ width: 64 }}>
            <PhoneIphoneIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              App
            </Typography>
          </Stack>
        </Tooltip>

        {groups.map(([group, groupTypes]) => (
          <React.Fragment key={group}>
            <Box sx={{ gridColumn: '1 / -1', pt: 1.5, pb: 0.5 }}>
              <Typography
                variant="caption"
                fontWeight={800}
                color="text.secondary"
                textTransform="uppercase"
                letterSpacing={0.5}
              >
                {group}
              </Typography>
              <Divider sx={{ mt: 0.5 }} />
            </Box>
            {groupTypes.map((type) => (
              <React.Fragment key={type.key}>
                <Box sx={{ py: 0.75 }}>
                  <Typography variant="body2" fontWeight={700}>
                    {type.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {type.description}
                  </Typography>
                </Box>
                <Stack alignItems="center" sx={{ width: 64 }}>
                  <Checkbox
                    checked={!!preferences[type.key]?.email}
                    onChange={() => toggle(type.key, 'email')}
                    inputProps={{
                      'aria-label': `${type.label} by email`,
                    }}
                  />
                </Stack>
                <Stack alignItems="center" sx={{ width: 64 }}>
                  <Checkbox
                    checked={!!preferences[type.key]?.app}
                    onChange={() => toggle(type.key, 'app')}
                    inputProps={{
                      'aria-label': `${type.label} in the app`,
                    }}
                  />
                </Stack>
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </Box>

      {saved && <Alert severity="success">Notification preferences saved.</Alert>}

      <Button
        variant="contained"
        onClick={handleSave}
        disabled={saving || !dirty}
        sx={{ alignSelf: 'flex-start', borderRadius: 2, fontWeight: 800 }}
      >
        {saving ? 'Saving…' : 'Save notification preferences'}
      </Button>
    </Stack>
  )
}

export default NotificationPreferences
